import { css, StyleDeclarationValue } from 'aphrodite';
import { observer } from 'mobx-react';
import { useImperativeHandle, useRef, useState } from 'react';
import {
	AppSupportedTimelineTypes,
	ContactViewModel,
	RichContentViewModel,
	TimelineEventTypes,
} from '../../../../../extViewmodels';
import { AllRichContentSupportedTypes, RichContentSupportedTypes } from '../../../../../models';
import { useUserSession } from '../../../../../models/hooks/appStateHooks';
import { getContactFullName } from '../../../../../models/utils';
import { isFilesFeatureEnabled } from '../../../../../models/utils/accountUtils';
import { invalidateContactTimeline } from '../../../../../queries';
import {
	ContactAutomationsProgressBanner,
	IContactAutomationsProgressBannerComponent,
} from '../../../../components/contacts/ContactAutomationsProgressBanner';
import { ContactConnections } from '../../../../components/contacts/ContactConnections';
import { ISelectOption, MultiSelect } from '../../../../components/DeprecatedSelect';
import { EntityDataBoards } from '../../../../components/dataBoards/EntityDataBoards';
import { EntityAnalysis } from '../../../../components/entities/EntityAnalysis';
import { EntityRichContent } from '../../../../components/entities/EntityRichContent';
import { AnalysisIcon } from '../../../../components/svgs/icons/AnalysisIcon';
import { ConnectionsIcon } from '../../../../components/svgs/icons/ConnectionsIcon';
import { DataboardsIcon } from '../../../../components/svgs/icons/DataboardsIcon';
import { FileTextIcon } from '../../../../components/svgs/icons/FileTextIcon';
import { NotesIcon } from '../../../../components/svgs/icons/NotesIcon';
import { TabContent, TabList, TabRoot, TabTrigger } from '../../../../components/Tabs';
import { ContactFiles } from './Files';
import { QuickActions } from './QuickActions';
import { styleSheet } from './styles';
import { ContactTimelineV2 } from './TimeLine';

const ALL_TIMELINE_FILTER_OPTIONS: ISelectOption<ITimelineCriteria>[] = [
	{
		dataContext: {
			name: 'all',
			type: 'All',
		},
		forceSelectAll: true,
		id: 'rich-content-filter-option-show-all',
		text: 'Show all',
		type: 'checkbox',
	},
	{
		dataContext: {
			name: 'notes',
			type: 'NoteEvent',
		},
		id: 'rich-content-filter-option-show-notes',
		text: 'Show notes',
		type: 'checkbox',
	},
	{
		dataContext: {
			name: 'action items',
			type: 'ActionItemEvent',
		},
		id: 'rich-content-filter-option-show-action-items',
		text: 'Show action items',
		type: 'checkbox',
	},
	{
		dataContext: {
			name: 'emails',
			type: ['SentEmailEvent', 'HtmlNewsletterEvent'],
		},
		id: 'rich-content-filter-option-show-emails',
		text: 'Show emails',
		type: 'checkbox',
	},
	{
		dataContext: {
			name: 'conversations',
			type: 'ConversationThreadEvent',
		},
		id: 'rich-content-filter-option-show-texts',
		text: 'Show texts',
		type: 'checkbox',
	},
	{
		dataContext: {
			name: 'activities outside Levitate',
			type: ['MeetingEvent', 'ExternalSentEmailEvent'],
		},
		id: 'rich-content-filter-option-show-activities-outside-levitate',
		text: 'Show activities outside Levitate',
		type: 'checkbox',
	},
	{
		dataContext: {
			name: 'phone calls',
			type: 'PhoneCallEvent',
		},
		id: 'rich-content-filter-option-show-phone-calls',
		text: 'Show phone calls',
		type: 'checkbox',
	},
	{
		dataContext: {
			name: 'surveys',
			type: 'SurveyResponseEvent',
		},
		id: 'rich-content-filter-option-show-surveys',
		text: 'Show surveys',
		type: 'checkbox',
	},
	{
		dataContext: {
			name: 'handwritten cards',
			type: 'HandwrittenCardOrderEvent',
		},
		id: 'rich-content-filter-option-show-handwritten-cards',
		text: 'Show handwritten cards',
		type: 'checkbox',
	},
];

const ALL_RICH_CONTENT_FILTER_OPTIONS: ISelectOption<IRichContentCriteria>[] = [
	{
		dataContext: {
			name: 'all',
			type: 'All',
		},
		forceSelectAll: true,
		id: 'rich-content-filter-option-show-all',
		text: 'Show all',
		type: 'checkbox',
	},
	{
		dataContext: {
			name: 'emails',
			type: 'EmailNote',
		},
		id: 'rich-content-filter-option-show-emails',
		text: 'Show emails',
		type: 'checkbox',
	},
	{
		dataContext: {
			name: 'notes',
			type: 'Note',
		},
		id: 'rich-content-filter-option-show-notes',
		text: 'Show notes',
		type: 'checkbox',
	},
	{
		dataContext: {
			name: 'action items',
			type: 'ActionItem',
		},
		id: 'rich-content-filter-option-show-action-items',
		text: 'Show action items',
		type: 'checkbox',
	},
];

const typesStorageKey = 'timelineFilterOptions';
const richContentStorageKey = 'richContentFilterOptions';
const expandedToggleStorageKey = 'timelineExpandedToggle';

export interface IContactActivityComponent {
	showRichContentTab(options?: { manageAutomationId?: string }): void;
}

enum ContactActivityDataContext {
	Analysis = 'Analysis',
	Connections = 'Connections',
	History = 'History',
	RichContent = 'RichContent',
	DataBoards = 'Data Boards',
	Timeline = 'Timeline',
	Files = 'Files',
}

export interface ITimelineCriteria {
	name: string;
	type: TimelineEventTypes | 'All' | TimelineEventTypes[];
}

export interface IRichContentCriteria {
	name: string;
	type: RichContentSupportedTypes | 'All';
}

interface Props {
	className?: StyleDeclarationValue[];
	contact?: ContactViewModel;
	innerRef?(ref?: IContactActivityComponent): void;
	setShouldStartPollingForRelationshipUpdate?(poll: boolean): void;
}

const getInitialRichContentTypes = () => {
	const storedTypes = window.localStorage.getItem(richContentStorageKey)?.split('|');
	const richContentTypes =
		!storedTypes || storedTypes.length === 0 || storedTypes.includes('All')
			? AllRichContentSupportedTypes
			: storedTypes.filter(option => option !== 'All');
	return richContentTypes as RichContentSupportedTypes[];
};

const getInitialTimelineSupportedTypes = () => {
	const storedTypes = window.localStorage.getItem(typesStorageKey)?.split('|');
	const timelineTypes =
		!storedTypes || storedTypes.length === 0 || storedTypes.includes('All')
			? AppSupportedTimelineTypes
			: storedTypes.filter(option => option !== 'All');

	return timelineTypes as TimelineEventTypes[];
};

const getDefaultTimelineOptions = (): ISelectOption<ITimelineCriteria>[] => {
	const storedOptionsStr = window.localStorage.getItem(typesStorageKey);
	if (!storedOptionsStr) {
		return ALL_TIMELINE_FILTER_OPTIONS;
	}
	const storedOptions = storedOptionsStr.split('|');
	if (storedOptions.includes('All')) {
		return ALL_TIMELINE_FILTER_OPTIONS;
	}
	return ALL_TIMELINE_FILTER_OPTIONS.filter(option => {
		if (Array.isArray(option.dataContext.type)) {
			return option.dataContext.type.some(x => storedOptions.includes(x));
		}
		return storedOptions.includes(option.dataContext.type);
	});
};

const getDefaultRichContentFilterOptions = (): ISelectOption<IRichContentCriteria>[] => {
	const storedOptions = window.localStorage.getItem(richContentStorageKey)?.split('|');
	return ALL_RICH_CONTENT_FILTER_OPTIONS.filter(option => {
		if (!storedOptions || storedOptions.length === 0) {
			return option;
		}

		return storedOptions.includes(option.dataContext.type);
	});
};

function _ContactActivity({ className, innerRef, contact, setShouldStartPollingForRelationshipUpdate }: Props) {
	const userSession = useUserSession();
	const contactAutomationsProgressBannerComponentRef = useRef<IContactAutomationsProgressBannerComponent>(null);
	const [selectedTab, setSelectedTab] = useState<ContactActivityDataContext>(
		userSession.account?.features?.timeline?.enabled
			? ContactActivityDataContext.Timeline
			: ContactActivityDataContext.RichContent
	);
	const [richContentTypes, setRichContentTypes] = useState<RichContentSupportedTypes[]>(getInitialRichContentTypes());
	const [timelineEventTypes, setTimelineEventTypes] = useState<TimelineEventTypes[]>(
		getInitialTimelineSupportedTypes()
	);
	const [expandAllTimeline, setExpandAllTimeline] = useState<boolean>(() => {
		const storedState = window.localStorage.getItem(expandedToggleStorageKey);
		return storedState === 'true';
	});

	useImperativeHandle(innerRef, () => {
		return {
			showRichContentTab: (options?: { manageAutomationId?: string }) => {
				setSelectedTab(ContactActivityDataContext.RichContent);
				if (options?.manageAutomationId) {
					contactAutomationsProgressBannerComponentRef.current?.showManageModal(options.manageAutomationId);
				}
			},
		};
	}, [setSelectedTab]);
	const canViewContactHistory =
		!!userSession?.user?.emailProviderFeatures?.calendar?.canViewHistoryByContact ||
		!!userSession.user?.emailProviderFeatures?.email?.canViewHistoryByContact;
	const shouldShowTimeline = !!userSession.account.features?.timeline?.enabled;
	const { policiesEnabled, donationsEnabled, opportunitiesEnabled } = userSession.account.features?.boards ?? {};
	const isFilesTabEnabled = isFilesFeatureEnabled(userSession.account);

	const handleExpandToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newState = e.target.checked;
		window.localStorage.setItem(expandedToggleStorageKey, newState.toString());
		setExpandAllTimeline(newState);
	};
	const onTimelineFilterClose = (selectedOptions: ISelectOption<ITimelineCriteria>[]) => {
		let selected: string[] = [];
		selectedOptions.forEach(option => {
			const eventType = option.dataContext.type;
			if (Array.isArray(eventType)) {
				selected = selected.concat(eventType);
				return;
			}
			selected.push(eventType);
		});
		window.localStorage.setItem(typesStorageKey, selected.join('|'));
		setTimelineEventTypes(getInitialTimelineSupportedTypes());
	};

	const getFilterOptionsTitle = (selectedOptions: ISelectOption<ITimelineCriteria | IRichContentCriteria>[]) => {
		if (selectedOptions.find(option => option.dataContext.type === 'All')) {
			return <div>Show all</div>;
		}

		const maxCharCount = 25;
		const text = `Show ${selectedOptions.map(option => option.dataContext.name).join(' and ')}`;
		return <div>{text.length > maxCharCount ? `${text.slice(0, maxCharCount)}...` : text}</div>;
	};
	const onRichContentFilterClose = (selectedOptions: ISelectOption<IRichContentCriteria>[]) => {
		const selected = selectedOptions.map(option => option.dataContext.type);
		window.localStorage.setItem(richContentStorageKey, selected.join('|'));
		setRichContentTypes(getInitialRichContentTypes());
	};
	return (
		<div className={css(styleSheet.contactActivity, className)}>
			<TabRoot
				className={css(styleSheet.tabRoot)}
				value={selectedTab}
				onValueChange={value => setSelectedTab(value as ContactActivityDataContext)}
			>
				<TabList styles={[styleSheet.tabs]}>
					{shouldShowTimeline ? (
						<TabTrigger variant='none' value={ContactActivityDataContext.Timeline} styles={[styleSheet.tabTrigger]}>
							<span className={css(styleSheet.contactActivityTabviewTabbarItemIcon)}>
								<NotesIcon fillColor='currentColor' />
							</span>
							<span className={css(styleSheet.contactActivityTabviewTabbarItemTitle)}>History</span>
						</TabTrigger>
					) : null}
					{!shouldShowTimeline ? (
						<TabTrigger variant='none' value={ContactActivityDataContext.RichContent} styles={[styleSheet.tabTrigger]}>
							<span className={css(styleSheet.contactActivityTabviewTabbarItemIcon)}>
								<NotesIcon fillColor='currentColor' />
							</span>
							<span className={css(styleSheet.contactActivityTabviewTabbarItemTitle)}>History</span>
						</TabTrigger>
					) : null}
					{!shouldShowTimeline && canViewContactHistory ? (
						<TabTrigger variant='none' value={ContactActivityDataContext.Analysis} styles={[styleSheet.tabTrigger]}>
							<span className={css(styleSheet.contactActivityTabviewTabbarItemIcon)}>
								<AnalysisIcon />
							</span>
							<span className={css(styleSheet.contactActivityTabviewTabbarItemTitle)}>Analysis</span>
						</TabTrigger>
					) : null}
					{userSession.userRole !== 'limitedUser' ? (
						<TabTrigger variant='none' value={ContactActivityDataContext.Connections} styles={[styleSheet.tabTrigger]}>
							<span className={css(styleSheet.contactActivityTabviewTabbarItemIcon)}>
								<ConnectionsIcon />
							</span>
							<span className={css(styleSheet.contactActivityTabviewTabbarItemTitle)}>Connections</span>
						</TabTrigger>
					) : null}
					{isFilesTabEnabled && (
						<TabTrigger
							variant='none'
							value={ContactActivityDataContext.Files}
							styles={[styleSheet.tabTrigger, styleSheet.tabTriggerFile]}
						>
							<span className={css(styleSheet.contactActivityTabviewTabbarItemIcon)}>
								<FileTextIcon />
							</span>
							<span className={css(styleSheet.contactActivityTabviewTabbarItemTitle)}>Files</span>
						</TabTrigger>
					)}
					{policiesEnabled || donationsEnabled || opportunitiesEnabled ? (
						<TabTrigger variant='none' value={ContactActivityDataContext.DataBoards} styles={[styleSheet.tabTrigger]}>
							<span className={css(styleSheet.contactActivityTabviewTabbarItemIcon)}>
								<DataboardsIcon fill='currentColor' />
							</span>
							<span className={css(styleSheet.contactActivityTabviewTabbarItemTitle)}>Data Boards</span>
						</TabTrigger>
					) : null}
				</TabList>
				<TabContent value={ContactActivityDataContext.Timeline} asChild>
					<>
						<QuickActions
							contact={contact?.toJs()}
							onContentCreated={() => {
								invalidateContactTimeline({ contactId: contact?.id });
							}}
						/>
						<ContactAutomationsProgressBanner
							contact={contact!}
							onRef={r => {
								contactAutomationsProgressBannerComponentRef.current = r;
							}}
						/>
						<div className={css(styleSheet.timelineFilterOptionsContainer)}>
							<label className={css(styleSheet.expandToggleLabel)}>
								<input
									type='checkbox'
									checked={expandAllTimeline}
									onChange={handleExpandToggle}
									className={css(styleSheet.expandToggleCheckbox)}
								/>
								Expand all
							</label>
							<MultiSelect
								onClose={onTimelineFilterClose}
								options={ALL_TIMELINE_FILTER_OPTIONS}
								dropdownStyles={[styleSheet.dropdown]}
								selectedOptions={getDefaultTimelineOptions()}
								selectAllIfNoneSelected={false}
								optionsStyle={{ maxHeight: 'auto' }}
								selectedOptionsTitle={getFilterOptionsTitle}
							/>
						</div>
						<ContactTimelineV2
							contactId={contact?.id}
							contactName={getContactFullName(contact!)}
							emailAddresses={contact?.emailAddresses}
							onDeleteRichContent={(richContent: RichContentViewModel) => contact?.deleteRichContent(richContent)}
							eventTypes={timelineEventTypes}
							expandAllTimeline={expandAllTimeline}
							setShouldStartPollingForRelationshipUpdate={setShouldStartPollingForRelationshipUpdate!}
						/>
					</>
				</TabContent>
				<TabContent value={ContactActivityDataContext.RichContent}>
					<div className={css(styleSheet.richContentFilterOptionsContainer)}>
						<MultiSelect
							onClose={onRichContentFilterClose}
							options={ALL_RICH_CONTENT_FILTER_OPTIONS}
							dropdownStyles={[styleSheet.dropdown]}
							selectedOptions={getDefaultRichContentFilterOptions()}
							selectAllIfNoneSelected={true}
							selectedOptionsTitle={getFilterOptionsTitle}
						/>
					</div>
					<ContactAutomationsProgressBanner
						contact={contact!}
						onRef={r => {
							contactAutomationsProgressBannerComponentRef.current = r;
						}}
					/>
					<EntityRichContent entity={contact} supportedTypes={richContentTypes} />
				</TabContent>
				<TabContent value={ContactActivityDataContext.Analysis}>
					<EntityAnalysis entity={contact!} />
				</TabContent>
				<TabContent value={ContactActivityDataContext.Connections}>
					<ContactConnections contact={contact!} />
				</TabContent>
				<TabContent value={ContactActivityDataContext.DataBoards}>
					<EntityDataBoards entityViewModel={contact} />
				</TabContent>
				<TabContent value={ContactActivityDataContext.Files}>
					<ContactFiles contactId={contact?.id} />
				</TabContent>
			</TabRoot>
		</div>
	);
}

export const ContactActivity = observer(_ContactActivity);
